import { LoadingCircle } from "../common";
import { fetchCMA } from "../../services/cma";
import { RiHomeSmile2Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import { CartComparisonTable } from "./cartComparisonTable";
import BuildingIcon from "../../assets/icons/buildingIcon.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import ChartBreakoutSquareIcon from "../../assets/icons/chartBreakoutSquareIcon.svg";
import { usePropertyStore } from "../../stores/newStores/cartComparisonMarketAnalysisStore";
import {
  IoArrowBackSharp,
  IoTrendingDown,
  IoTrendingUp,
} from "react-icons/io5";
import { useShowLoginModalStore } from "../../stores/loginModalStore";
import { useAuth } from "../../context/authProvider";
import { useMapStore } from "../../stores/mapCordinations";

const CartComparison = () => {
  const { zlid } = useParams<{ zlid: string }>();
  const { setProperties, properties } = usePropertyStore();
  const { setShowLoginModal } = useShowLoginModalStore();
  const { authenticated } = useAuth();
  const [lowPrice, setLowPrice] = useState<string>("N/A");
  const [highPrice, setHighPrice] = useState<string>("N/A");
  const [medianPrice, setMedianPrice] = useState<string>("N/A");
  const [meanPrice, setMeanPrice] = useState<string>("N/A");
  const [comparableSales, setComparableSales] = useState<string>("N/A");
  const [avgDom, setAvgDom] = useState<string>("N/A");
  const mountedRef = useRef(false);
  const navigate = useNavigate();
  const { setShowProperty } = useMapStore();
  const [loading, setLoading] = useState(false);
  function formatPrice(price: number) {
    return typeof price === "number" ? price.toLocaleString() : price;
  }

  const fetchProperties = useCallback(async () => {
    if (!zlid) {
      console.error("ZLID is undefined");
      return;
    }
    try {
      setLoading(true);
      const response = await fetchCMA(zlid);

      if (response.listings) {
        if (response.statistics) {
          const { soldPrice, daysOnMarket } =
            response.statistics.statistics ?? {};
          // setLowPrice(
          //   soldPrice?.min
          //     ? `$${formatPrice(convertToFormattedAmount(soldPrice.min))}`
          //     : "N/A"
          // );
          // setHighPrice(
          //   soldPrice?.max
          //     ? `$${formatPrice(convertToFormattedAmount(soldPrice.max))}`
          //     : "N/A"
          // );
          // setMedianPrice(
          //   soldPrice?.med
          //     ? `$${formatPrice(convertToFormattedAmount(soldPrice.med))}`
          //     : "N/A"
          // );
          // setMeanPrice(
          //   soldPrice?.avg
          //     ? `$${formatPrice(convertToFormattedAmount(soldPrice.avg))}`
          //     : "N/A"
          // );
          setComparableSales(response.statistics.count?.toString() ?? "N/A");
          setAvgDom(daysOnMarket?.avg?.toFixed(0) ?? "N/A");
        }



        const reorderedProperties = [...response.listings];
        const index = reorderedProperties.findIndex(
          (property) => property.listingId === zlid
        );

        if (index > -1) {
          const [matchedProperty] = reorderedProperties.splice(index, 1);
          reorderedProperties.unshift(matchedProperty);
        }

        const uniqueProperties = reorderedProperties.filter(
          (property, index, self) =>
            index === self.findIndex((p) => p.listingId === property.listingId)
        );

        setProperties(uniqueProperties);
        setLoading(false);
      } else {
        setProperties([]);
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch listing data", error);
      setLoading(false);
    }
  }, [
    zlid,
    lowPrice,
    highPrice,
    medianPrice,
    meanPrice,
    comparableSales,
    avgDom,
  ]);

  useEffect(() => {
    const prices = properties
      .map((property) => property.listPrice)
      .filter((price) => typeof price === "number");
    const sortedPrices = [...prices].sort((a, b) => a - b);
  
    const lowPrice = Math.min(...prices);
    const highPrice = Math.max(...prices);
    const meanPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const medianPrice =
      prices.length % 2 === 0
        ? (sortedPrices[prices.length / 2 - 1] + sortedPrices[prices.length / 2]) / 2
        : sortedPrices[Math.floor(prices.length / 2)];
  
    console.log("prices", lowPrice, highPrice, meanPrice.toFixed(2), medianPrice);
  
    const formatWithTwoDecimals = (value: number) =>
      parseFloat(value.toFixed(2)); 
  
    setLowPrice(
      lowPrice ? `$${formatPrice(formatWithTwoDecimals(convertToFormattedAmount(lowPrice)))}` : "N/A"
    );
  
    setHighPrice(
      highPrice ? `$${formatPrice(formatWithTwoDecimals(convertToFormattedAmount(highPrice)))}` : "N/A"
    );
  
    setMedianPrice(
      medianPrice ? `$${formatPrice(formatWithTwoDecimals(convertToFormattedAmount(medianPrice)))}` : "N/A"
    );
  
    setMeanPrice(
      meanPrice ? `$${formatPrice(formatWithTwoDecimals(convertToFormattedAmount(meanPrice)))}` : "N/A"
    );
  }, [properties]);
  
  
  useEffect(() => {
    if (!authenticated) {
      setShowLoginModal(true);
    }
  }, [authenticated]);
  useEffect(() => {
    if (!mountedRef.current && authenticated) {
      mountedRef.current = true;
      fetchProperties();
    }
  }, [fetchProperties, authenticated]);

  const data = [
    { label: "Highest Price", value: highPrice, icon: <IoTrendingUp /> },
    {
      label: "Lowest Price",
      value: lowPrice,
      icon: <IoTrendingDown className="text-xl text-[rgba(50,48,47,0.8)]" />,
    },
    {
      label: "Average Price",
      value: meanPrice,
      icon: <img src={BuildingIcon} alt="building icon" />,
    },
    {
      label: "Median Price",
      value: medianPrice,
      icon: <img src={BuildingIcon} alt="building icon" />,
    },
    {
      label: "Comparable Sales",
      value: `${comparableSales} Sales`,
      icon: <RiHomeSmile2Line className="text-xl" />,
    },
    {
      label: "Average Days on Market",
      icon: <RiHomeSmile2Line className="text-xl" />,
      value: `${avgDom} Days`,
    },
  ];

  const findAddress = properties.find((item) => item.listingId === zlid);

  return (
    <div className="">
      <div className="flex flex-col">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div
          className="cursor-pointer flex items-center gap-2 p-1 mb-3"
          onClick={() => {
            setShowProperty(() => true);
            navigate(`/listings/${zlid}`);
          }}
        >
          <IoArrowBackSharp className="text-xl" />
          <span>Back</span>
        </div>
        <span>{findAddress?.address}</span>
        <div className="text-z-shark text-[40px] font-semibold">
          Comparative Market Analysis
        </div>
        {!loading && (
          <>
            <div className="flex items-center mt-4 gap-1">
              <span className="text-2xl text-z-shark">
                Comparable Statistics
              </span>
              <img src={ChartBreakoutSquareIcon} alt="square icon" />
            </div>
            <div className="grid grid-cols-6 gap-1 mb-9 mt-4">
              {data.map((item, index: number) => (
                <div
                  key={index}
                  className="w-full flex py-4 px-3 xl:px-4 justify-between flex-col border border-[rgba(50,48,47,0.50)] h-[90px] rounded-lg"
                >
                  <div className="flex items-center gap-1">


                    <span className="font-semibold text-xs xl:text-sm text-[rgba(50,48,47,0.80)]">
                      {item.label}
                    </span>
                    {item.icon}

                  </div>
                  <span className="text-xl xl:text-2xl font-bold text-z-shark">
                    {typeof item.value === "number"
                      ? `$${formatPrice(item.value)}`
                      : item.value}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div
        className={`w-full text-black space-y-6 pb-6 ${loading || properties.length === 0 ? "h-full" : ""
          } bg-white`}
      >
        {loading && (
          <div className="flex flex-col items-center justify-center h-full">
            <LoadingCircle />
          </div>
        )}
        {!loading && properties.length === 0 && (
          <div className="flex items-center text-center justify-center flex-col h-full w-full px-4 space-y-8 mt-40">
            <div className="space-y-3">
              <div className="font-bold text-4xl">Oops !</div>
              <div className="text-3xl">
                No added <span className="text-[#FF5757]">properties</span> to
                compare
              </div>
            </div>
            <button
              type="button"
              className="bg-z-dark-moss-green shadow-xl text-white w-[31.25rem] font-bold text-xl py-3.5 rounded-full"
              onClick={() => navigate("/")}
            >
              Add homes
            </button>
          </div>
        )}
        {zlid && !loading && <CartComparisonTable zlid={zlid} />}
      </div>
    </div>
  );
};

export { CartComparison };
